import type { components } from '@scalty/contracts'
import { DATA_WORDS } from '~/utils/state'
import { CUSTOMER_TERMS } from '~/utils/terms'

type S = components['schemas']
/** The two facts the contract keeps apart: was the file read, and is its text retrievable. */
export type MemberFacts = {
  state: S['DatasetMemberState']
  memoryState: S['DatasetMemberMemoryState']
  /** When the platform will try again by itself. Null once it has stopped, so the portal
      never promises a retry that is not coming. */
  memoryRetryAt?: string | null
}
const retrying = (member: MemberFacts) => Boolean(member.memoryRetryAt)

/**
 * One word per file. Reading and retrievability are separate facts in the contract, and the
 * customer has one question of them: can a model trained here use this file, and can it quote
 * it. A document that has been read but is not retrievable yet is NOT "Ready" — saying so would
 * claim a state the platform has not reached. Memory is not a setting any more (founder,
 * 2026-09-21): it is how documents work, so its progress is simply part of the file's state.
 */
export function memberStateWord(member: MemberFacts): string {
  /* 'stale' outlives the reading state on purpose: whatever the current file is doing, the
     previous one is what a model quotes today, and that is the fact the customer needs. */
  /* A file the project deleted is gone whatever its last reading or memory state said, so the
     deletion is the word — checked before `stale`, which would otherwise promise quotable text
     from a file that no longer exists (W8-UI-10). */
  if (member.state === 'removed') return DATA_WORDS.member.removed
  if (member.memoryState === 'stale') return OLDER_TEXT
  if (member.state !== 'ready') return DATA_WORDS.member[member.state]
  switch (member.memoryState) {
    case 'pending': return CUSTOMER_TERMS.states.preparing
    case 'failed': return NOT_QUOTABLE
    /* 'unsupported' (tabular and conversations, never quotable) and 'ready' both mean nothing
       is outstanding, and a server that does not answer the field leaves the reading word. */
    default: return DATA_WORDS.member[member.state]
  }
}
export const NOT_QUOTABLE = 'Not quotable'
export const OLDER_TEXT = 'Older text'

/**
 * The row's own line about quoting, in the portal's words. The contract carries the platform's
 * sentence in `memoryError`, but it names the search projection — implementation vocabulary the
 * portal does not put on screen — so the causes the platform distinguishes are said here
 * instead: a file that was refused has to be corrected, a file whose text could not be made
 * searchable is retried by the platform itself, and one it has given up on has to be read
 * again. The line shares a row with the file's size and is cut off past roughly forty
 * characters, so what to do about a failure that stays is said once under the table.
 */
export function memberQuotingNote(member: MemberFacts): string {
  /* The platform separates a deleted source from one it could not read: telling a customer to
     have a deleted file read again is an action that cannot be taken (W8-UI-10). */
  if (member.state === 'removed') return 'removed · take it out of the dataset'
  if (member.memoryState === 'stale') {
    return retrying(member) ? 'older text quoted · we keep trying' : 'older text quoted · read it again'
  }
  if (member.memoryState === 'pending' && member.state === 'ready') return 'being made quotable'
  if (member.memoryState !== 'failed') return ''
  if (member.state === 'refused') return 'correct or replace the file'
  return retrying(member) ? 'not quotable yet · we keep trying' : 'not quotable · read the file again'
}

/**
 * Said once under the files, and only when a file is actually in that state. Each one says
 * what the state word means before it says what to do about it: "not quotable" and "older
 * text" are the platform's own words, and a customer meets them here first (W8-UI-07).
 */
export const UNQUOTABLE_FOOTER = 'Not quotable: the file is still trained on, but a model cannot cite it in an answer. If one stays that way, tell support which file it is.'
export const OLDER_TEXT_FOOTER = 'Older text: the newest upload of this file cannot be searched yet, so answers quote the version before it.'
